import { Text, Group, MantineTheme, Select } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import type { CSSObject as CSS } from "@mantine/emotion";
import { FC } from "react";

type Props = {
  labelText: string;
  id?: string;
  placeHolder?: string;
  onChange?: CallableFunction;
  style?: CSS;
  isErrored?: string;
  errorMessage?: boolean;
  // Accepts a plain label list (the common case) or {value,label} pairs when the
  // caller must key options by a stable id rather than the display label — e.g. a
  // directory where two entries can share a name. Mantine's Select reports `value`
  // (the id) to onChange in the object form and the label in the string form, so
  // callers using pairs get the id back, not the name. Backward-compatible: every
  // existing string[] call site is unchanged.
  data: string[] | { value: string; label: string }[];
  value: string | null;
  searchable?: boolean;
  disabled?: boolean;
  /**
   * Render the dropdown in a portal on document.body instead of inline.
   * Needed inside a Modal, or under any ancestor with overflow/transform/a
   * lower stacking context, where an inline dropdown gets clipped or hidden.
   * True by default — matches Mantine's own Combobox default, which every
   * existing call site relied on before this prop existed.
   */
  withinPortal?: boolean;
  maxDropdownHeight?: number;
};

const InputSelect: FC<Props> = ({
  labelText,
  id,
  placeHolder,
  onChange,
  style: _style,
  isErrored,
  data,
  value,
  searchable = true,
  disabled = false,
  errorMessage,
  withinPortal = true,
  maxDropdownHeight,
}) => {
  const useStyles = createStyles((_theme: MantineTheme) => ({
    mainContainer: {},
    text: {
      color: "#3C4257",
      textAlign: "left",
    },
    inputField: {
      padding: "2px",
      height: "45px",
      width: "100%",
      marginBottom: "10px",

      //border:props.isErrored?'1px red solid':''
    },
    errorMessage: {
      color: "red",
      position: "relative",
      fontSize: "14px",
      top: "-10px",
      left: "0px",
    },
  }));
  const { classes } = useStyles();

  return (
    <div className={classes.mainContainer}>
      <Group justify="flex-start" mb={5}>
        <Text className={classes.text} component="label" htmlFor={id} size="sm">
          {labelText}
        </Text>
      </Group>
      <Select
        // Forward `id` to the underlying <input> so the sibling <label htmlFor={id}>
        // actually points at it — enables getByLabel() in tests and screen readers,
        // instead of Mantine auto-generating a random id the label can't reach.
        id={id}
        className={classes.inputField}
        placeholder={placeHolder}
        size="md"
        searchable={searchable}
        value={value}
        disabled={disabled}
        data={data}
        comboboxProps={{ withinPortal }}
        maxDropdownHeight={maxDropdownHeight}
        onChange={(e: string | null) => {
          if (onChange && e !== null) {
            onChange(e);
          }
        }}
        styles={{
          option: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          },
          input: {
            border: isErrored ? "1px solid #fa5252" : undefined,
          },
        }}
      />

      {isErrored && errorMessage && <div className={classes.errorMessage}>{isErrored}</div>}
    </div>
  );
};

export default InputSelect;
